import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Loading, Page, Section, useFail, useToast } from "../components/ui.tsx";
import { api, isQueued } from "../lib/api.ts";
import { dateLong, dateShort, daysUntil, label, time, when } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";

export function Today() {
  const me = useMe();
  const q = useQuery({ queryKey: ["today"], queryFn: () => api.get("/today"), refetchInterval: 120_000 });
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  if (q.isLoading) return <Page title="Today"><Loading /></Page>;
  if (q.error) return <Page title="Today"><ErrorView error={q.error} /></Page>;
  const t = q.data;
  const first = me.user.name.split(" ")[0];
  const hour = Number(new Date().toLocaleString("en-AU", { hour: "numeric", hour12: false, timeZone: "Australia/Brisbane" }));
  const greet = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";
  const overdue = t.tasks.filter((x: any) => x.overdue).length;

  const complete = async (task: any) => {
    try {
      const r = await api.patch(`/tasks/${task.id}`, { status: "done" }, task.version, { offline: `Tick off "${task.title}"` });
      toast(isQueued(r) ? "Saved on this phone. It'll sync when you're back in range." : "Done");
      qc.invalidateQueries({ queryKey: ["today"] });
    } catch (e) { fail(e); }
  };

  return (
    <Page title="Today">
      <div className="hero">
        <div className="greet">{dateLong(t.date)} · {greet}, {first}.</div>
        {t.roster && (
          <div className={"roster" + (t.roster.on_site ? " on" : "")}>
            <span className="sq" />{t.roster.label}<span className="muted">· until {dateShort(t.roster.changes_on)}</span>
          </div>
        )}
      </div>

      <div className="stats">
        <Link to="/inbox?filter=needs_reply" className={"stat" + (t.waiting.length ? " hot" : "")}><b className="num">{t.waiting.length}</b><span>Waiting</span></Link>
        <div className={"stat" + (t.enquiries.length ? " hot" : "")}><b className="num">{t.enquiries.length}</b><span>Enquiries</span></div>
        <Link to="/tasks" className="stat"><b className="num">{t.tasks.length}{overdue > 0 && <small className="danger"> {overdue} late</small>}</b><span>Tasks due</span></Link>
        <div className="stat"><b className="num">{t.appointments.filter((a: any) => a.day_offset === 0).length}</b><span>Today</span></div>
      </div>

      {t.draftsWaiting > 0 && (
        <div className="banner blue"><Icon name="lock" size={18} /><span><b>{t.draftsWaiting} draft{t.draftsWaiting > 1 ? "s" : ""} waiting for approval.</b> Sending is off in this build, so nothing has gone out.</span></div>
      )}

      <Section title="Appointments" count={t.appointments.length}>
        {t.appointments.length === 0 ? <Empty>Nothing in your calendar today or tomorrow.</Empty> : (
          <div className="list">
            {t.appointments.map((a: any) => (
              <Link key={a.id} to={a.person_id ? `/people/${a.person_id}` : a.listing_id ? `/listings/${a.listing_id}` : "#"} className="row">
                <div className="time">{time(a.starts_at)}<small>{a.day_offset === 0 ? "Today" : "Tomorrow"}</small></div>
                <div className="main-col"><div className="title">{a.title}</div><div className="sub">{a.location ?? label(a.kind)}</div></div>
                <Icon name="chevron" className="chev" />
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Waiting on you" count={t.waiting.length} more={<Link to="/inbox?filter=needs_reply">Inbox</Link>}>
        {t.waiting.length === 0 ? <Empty>No one is waiting on a reply.</Empty> : (
          <div className="list">
            {t.waiting.map((c: any) => (
              <Link key={c.id} to={`/inbox/${c.id}`} className={"row" + (c.unread_count ? " unread" : "")}>
                <div className="main-col"><div className="title">{c.name}</div><div className="sub">{label(c.channel)} · {c.preview}</div></div>
                <span className="when">{when(c.last_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="New enquiries" count={t.enquiries.length}>
        {t.enquiries.length === 0 ? <Empty>No new enquiries this week.</Empty> : (
          <div className="list">
            {t.enquiries.map((e: any) => (
              <Link key={e.id} to={`/people/${e.person_id}`} className="row">
                <div className="main-col"><div className="title">{e.person_name}</div>
                  <div className="sub">{e.pipeline === "seller" ? "Seller lead" : "Buyer"}{e.about ? ` · ${e.about}` : ""}{e.source ? ` · ${e.source}` : ""}</div></div>
                <span className="when">{when(e.enquiry_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Tasks due" count={t.tasks.length} more={<Link to="/tasks">All tasks</Link>}>
        {t.tasks.length === 0 ? <Empty>Nothing due. Nice.</Empty> : (
          <div className="list">
            {t.tasks.map((x: any) => (
              <div key={x.id} className={"row" + (x.overdue ? " overdue" : "")}>
                <button className="iconbtn" aria-label={`Done: ${x.title}`} onClick={() => complete(x)}><Icon name="check" /></button>
                <Link to={x.person_id ? `/people/${x.person_id}` : x.listing_id ? `/listings/${x.listing_id}` : "/tasks"} className="main-col">
                  <div className="title">{x.title}</div>
                  <div className="sub">{[x.person_name, x.listing_address].filter(Boolean).join(" · ") || label(x.priority)}</div>
                </Link>
                <span className="when">{x.overdue ? `${-daysUntil(x.due_on)}d late` : "Today"}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Key dates this week" count={t.keyDates.length}>
        {t.keyDates.length === 0 ? <Empty>No key dates in the next seven days.</Empty> : (
          <div className="list">
            {t.keyDates.map((k: any) => {
              const d = daysUntil(k.on_date);
              return (
                <Link key={k.id + k.on_date} to={k.person_id ? `/people/${k.person_id}` : k.listing_id ? `/listings/${k.listing_id}` : "#"} className="row">
                  <div className="main-col"><div className="title">{label(k.kind)}{k.person_name ? `: ${k.person_name}` : ""}</div>
                    <div className="sub">{k.address ?? k.label}</div></div>
                  <span className={"when" + (d <= 1 ? " blue" : "")}>{d === 0 ? "Today" : d === 1 ? "Tomorrow" : dateShort(k.on_date)}</span>
                </Link>
              );
            })}
          </div>
        )}
      </Section>

      {t.nudges.length > 0 && (
        <Section title="Gone quiet" count={t.nudges.length} more={<Link to="/pipelines">Pipelines</Link>}>
          <div className="list">
            {t.nudges.map((n: any) => (
              <Link key={n.id} to="/pipelines" className="row">
                <div className="main-col"><div className="title">{n.person_name}</div>
                  <div className="sub">{n.reason === "check_in" ? `Nurture check-in due ${dateShort(n.next_follow_up_on)}` : `Quiet too long in ${n.stage}`} · {n.title}</div></div>
                <Icon name="chevron" className="chev" />
              </Link>
            ))}
          </div>
        </Section>
      )}
      <div style={{ height: 24 }} />
    </Page>
  );
}
